import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { useToast } from '@/components/Toast';
import { SeverityBadge, StatusBadge, FloorBadge } from '@/components/badges';
import { DeveloperPicker } from '@/components/DeveloperPicker';
import { getTriage, claimIncident, assignIncident, resolveIncident, setTriageNote } from '@/services/alerts';
import { getIncident } from '@/services/incidents';
import { getPcs, getRooms, shortId } from '@/services/refdata';
import { relativeTime } from '@/lib/format';
import type { Incident, IncidentTriage } from '@/types';

export function IncidentDetailPage() {
  const { id = '' } = useParams();
  const { developer } = useAuth();
  const toast = useToast();
  const [triage, setTriage] = useState<IncidentTriage | null>(null);
  const [incident, setIncident] = useState<Incident | null>(null);
  const [rooms, setRooms] = useState<Map<string, string>>(new Map());
  const [pcs, setPcs] = useState<Map<string, string>>(new Map());
  const [assignee, setAssignee] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getTriage(id).then((t) => { setTriage(t); setNote(t?.note ?? ''); }).catch((e) => toast(e.message, 'error'));
    getIncident(id).then(setIncident).catch(() => {});
    getRooms().then(setRooms).catch(() => {});
    getPcs().then(setPcs).catch(() => {});

    const ch = supabase
      .channel(`incident-${id}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'alerts', table: 'incident_triage', filter: `incident_id=eq.${id}` },
        (payload: any) => setTriage(payload.new))
      .subscribe();
    return () => { supabase.removeChannel(ch); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function run(fn: () => Promise<unknown>, okMsg: string) {
    setBusy(true);
    try { await fn(); toast(okMsg, 'success'); const t = await getTriage(id); setTriage(t); }
    catch (e: any) { toast(e.message ?? 'Action failed', 'error'); }
    finally { setBusy(false); }
  }

  if (!triage) return <div className="page"><Link to="/inbox" className="back">← Inbox</Link><div className="muted">Loading…</div></div>;

  const meta = incident?.metadata ?? {};
  return (
    <div className="page">
      <Link to="/inbox" className="back">← Inbox</Link>
      <div className="detail-head">
        <div className="row-badges">
          <SeverityBadge severity={triage.severity} />
          {triage.is_floor && <FloorBadge />}
          <StatusBadge status={triage.status} />
          <span className="chip">{triage.incident_type}</span>
          {triage.category && <span className="chip chip-dim">{triage.category}</span>}
        </div>
        <h1>{triage.message ?? '(no message)'}</h1>
        <div className="muted">
          {rooms.get(triage.room_id ?? '') ?? shortId(triage.room_id)} · PC {pcs.get(triage.pc_instance_id ?? '') ?? shortId(triage.pc_instance_id)} · {relativeTime(triage.occurred_at)}
          {triage.source && <> · {triage.source}</>}
        </div>
      </div>

      <div className="cols">
        <div className="card">
          <h3>Actions</h3>
          <div className="action-block">
            {!triage.claimed_by
              ? <button className="btn btn-primary" disabled={busy} onClick={() => run(() => claimIncident(id), 'You are investigating this')}>I'm on it</button>
              : <div className="muted">Claimed by {triage.claimed_by === developer?.id ? 'you' : shortId(triage.claimed_by)}</div>}
          </div>
          <div className="action-block">
            <label>Assign to</label>
            <div className="inline">
              <DeveloperPicker value={assignee} onChange={setAssignee} excludeId={developer?.id} />
              <button className="btn" disabled={busy || !assignee} onClick={() => run(() => assignIncident(id, assignee!), 'Assigned')}>Assign</button>
            </div>
          </div>
          <div className="action-block inline">
            <button className="btn btn-good" disabled={busy} onClick={() => run(() => resolveIncident(id, 'resolved'), 'Resolved')}>Resolve</button>
            <button className="btn btn-ghost" disabled={busy} onClick={() => run(() => resolveIncident(id, 'wont_fix'), "Marked won't-fix")}>Won't fix</button>
          </div>
          <div className="action-block">
            <label>Shared note</label>
            <textarea className="input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
            <button className="btn btn-sm" disabled={busy} onClick={() => run(() => setTriageNote(id, note), 'Note saved')}>Save note</button>
          </div>
          {incident?.log_url && (
            <div className="action-block">
              <a className="btn btn-ghost" href={incident.log_url} target="_blank" rel="noreferrer">⬇ Log bundle</a>
            </div>
          )}
        </div>

        <div className="card">
          <h3>Incident metadata</h3>
          {incident ? (
            <pre className="json">{JSON.stringify(meta, null, 2)}</pre>
          ) : (
            <div className="muted">Source incident not loaded (it may have aged out).</div>
          )}
        </div>
      </div>
    </div>
  );
}
